/* Acceptation invitation parent/tuteur (roadmap-v1 bloc 03, lot 2B). */
/* Token en query → aperçu → mot de passe + CGU → crée le compte + connexion. */

import { useEffect, useState, type FormEvent } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useNavigate } from '../lib/routing';
import { api } from '../lib/api';
import { signIn } from '../lib/session';
import { isStrongPassword, PASSWORD_MIN_LENGTH, PASSWORD_TOO_SHORT_MESSAGE } from '../lib/password';
import { FormField } from '../components/modal';
import Header from '../components/header';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface Preview {
  email: string;
  children: Array<{ first_name: string; last_name: string }>;
  account_exists: boolean;
}

export default function ResponsableAccept() {
  const search = useSearch({ strict: false }) as Record<string, string>;
  const token = search.token ?? '';
  const navigate = useNavigate();

  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [cgu, setCgu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    api.parentOrTutor.invitationByToken
      .query({ token })
      .then((p) => setPreview(p as Preview | null))
      .catch(() => setPreview(null))
      .finally(() => setLoading(false));
  }, [token]);

  /* Compte déjà existant : connexion + rattachement du nouvel élève. */
  const submitExisting = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!preview || !password) return;
    setBusy(true);
    try {
      const { error: sErr } = await signIn(preview.email, password);
      if (sErr) {
        setError('Connexion impossible. Vérifiez votre mot de passe.');
        setBusy(false);
        return;
      }
      await api.parentOrTutor.claim.mutate({ token });
      navigate('/tutor');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  /* Nouveau compte : création + connexion. */
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isStrongPassword(password)) {
      setError(PASSWORD_TOO_SHORT_MESSAGE);
      return;
    }
    if (!cgu) {
      setError("Vous devez accepter les conditions d'utilisation.");
      return;
    }
    if (!preview) return;
    setBusy(true);
    try {
      await api.parentOrTutor.accept.mutate({ token, password, cguAccepted: cgu });
      const { error: sErr } = await signIn(preview.email, password);
      if (sErr) {
        setError('Compte créé, mais la connexion a échoué. Connectez-vous manuellement.');
        setBusy(false);
        return;
      }
      navigate('/tutor');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const invalid = !token || (!loading && !preview);

  return (
    <>
      <Header />
      <main className="max-w-[480px] mx-auto mt-10 px-6 pb-16">
        <div className="bg-card rounded-card shadow-card w-full p-6">
          <h1 className="mt-0 text-[22px] text-foreground">Activer mon accès parent / tuteur</h1>

          {invalid ? (
            <Alert>
              Lien invalide ou expiré. Demandez un nouvel envoi à l'établissement.
            </Alert>
          ) : loading ? (
            <p>Chargement…</p>
          ) : preview!.account_exists ? (
            <form onSubmit={submitExisting} className="flex flex-col gap-[14px]">
              <p className="text-muted-foreground">
                Vous avez déjà un compte <strong>{preview!.email}</strong>. Connectez-vous pour ajouter
                {preview!.children.length > 0 ? (
                  <> {preview!.children.map((c) => `${c.first_name} ${c.last_name}`).join(', ')}</>
                ) : (
                  ' ce nouvel élève'
                )}
                {' '}à votre espace.
              </p>

              <FormField
                label="Mot de passe"
                value={password}
                onChange={setPassword}
                type="password"
                placeholder="Votre mot de passe"
                autoFocus
              />

              {error && <Alert>{error}</Alert>}

              <Button type="submit" disabled={busy || !password}>
                {busy ? 'Connexion…' : "Se connecter et ajouter l'élève"}
              </Button>
            </form>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-[14px]">
              <p className="text-muted-foreground">
                Invitation pour <strong>{preview!.email}</strong>
                {preview!.children.length > 0 && (
                  <>
                    {' '}— enfant(s) :{' '}
                    {preview!.children.map((c) => `${c.first_name} ${c.last_name}`).join(', ')}
                  </>
                )}
                .
              </p>

              <FormField
                label="Choisissez un mot de passe"
                value={password}
                onChange={setPassword}
                type="password"
                placeholder={`${PASSWORD_MIN_LENGTH} caractères minimum`}
                autoFocus
              />

              <label className="flex gap-[10px] items-start text-sm">
                <input type="checkbox" checked={cgu} onChange={(ev) => setCgu(ev.target.checked)} className="mt-[3px]" />
                <span>
                  J'accepte les{' '}
                  <a href="/cgu" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-naia-blue underline">conditions d'utilisation</a>
                  {' '}et la{' '}
                  <a href="/confidentialite" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-naia-blue underline">politique de confidentialité</a>
                  {' '}de Naïa.
                </span>
              </label>

              {error && <Alert>{error}</Alert>}

              <Button type="submit" disabled={busy || !cgu || !isStrongPassword(password)}>
                {busy ? 'Activation…' : 'Activer mon accès'}
              </Button>
            </form>
          )}
        </div>
      </main>
    </>
  );
}
